import { DateTime, Duration } from "luxon"
import { useMemo } from "react"

import { parseDateTimeFromApi } from "../../lib/time"
import { useAppSelector } from "../store/storeHooks"
import { selectSelectedLocale } from "./localizationSelectors"
import { useAppLocalization } from "./components/AppLocalizationProvider"

const ISO_TIME_MINUTE_FORMAT = "HH:mm"
const API_DATETIME_FORMAT = "yyyy-MM-dd HH:mm"
const ISO_DATETIME_MINUTE_FORMAT = "yyyy-MM-dd'T'HH:mm"
const ISO_DATETIME_FORMAT = "yyyy-MM-dd"

function capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

interface ShortRelativeUnits {
    s: string
    m: string
    h: string
    d: string
    w: string
    mo: string
    y: string
}

function parseShortRelativeUnits(value: string): ShortRelativeUnits {
    const [s, m, h, d, w, mo, y] = value.split("|")
    return { s, m, h, d, w, mo, y }
}

interface DateTimeFactoryArguments {
    locale: string
    dateFormat: string
    longDateFormat: string
    weekdayLongDateFormat: string
    dayAndDateFormat: string
    timeFormat: string
    timeFormatHms: string
    dateTimeFormat: string
    durationHourMinuteFormat: string
    durationMinuteFormat: string
    durationSecondsFormat: string
    compactPeriod: string
    hourCycle: string
    shortRelativeUnits: string
    invalidDateText: string
}

type HourCycle = "h11" | "h12" | "h23" | "h24" | undefined

function isHourCycle(value: string | undefined): value is HourCycle {
    switch (value) {
        case "h11":
        case "h12":
        case "h23":
        case "h24":
            return true
        default:
            return false
    }
}

export class DateTimeFactory {
    private locale: string
    private longDateFormat: string
    private weekdayLongDateFormat: string
    private dateFormat: string
    private dayAndDateFormat: string
    private timeFormat: string
    private timeFormatHms: string
    private dateTimeFormat: string
    private durationHourMinuteFormat: string
    private durationMinuteFormat: string
    private durationSecondsFormat: string
    private compactPeriod: string
    private hourCycle: HourCycle
    private shortRelativeUnits: ShortRelativeUnits
    private invalidDateText: string

    constructor({
        locale,
        dateFormat,
        longDateFormat,
        weekdayLongDateFormat,
        dayAndDateFormat,
        timeFormat,
        timeFormatHms,
        dateTimeFormat,
        durationHourMinuteFormat,
        durationSecondsFormat,
        durationMinuteFormat,
        compactPeriod,
        hourCycle,
        shortRelativeUnits,
        invalidDateText,
    }: DateTimeFactoryArguments) {
        this.locale = locale
        this.dateFormat = dateFormat
        this.longDateFormat = longDateFormat
        this.weekdayLongDateFormat = weekdayLongDateFormat
        this.dayAndDateFormat = dayAndDateFormat
        this.timeFormat = timeFormat
        this.timeFormatHms = timeFormatHms
        this.dateTimeFormat = dateTimeFormat
        this.durationHourMinuteFormat = durationHourMinuteFormat
        this.durationMinuteFormat = durationMinuteFormat
        this.durationSecondsFormat = durationSecondsFormat
        this.compactPeriod = compactPeriod
        this.hourCycle = isHourCycle(hourCycle) ? hourCycle : "h12"
        this.shortRelativeUnits = parseShortRelativeUnits(shortRelativeUnits)
        this.invalidDateText = invalidDateText
    }

    now() {
        return DateTime.local().setLocale(this.locale)
    }

    fromISO(dateTime: string) {
        return DateTime.fromISO(dateTime, { locale: this.locale })
    }

    fromMillis(value: number) {
        return DateTime.fromMillis(value, { locale: this.locale })
    }

    fromJSDate(date: Date, options?: Readonly<{ zone?: string }>) {
        return DateTime.fromJSDate(date, { zone: options?.zone }).setLocale(this.locale)
    }

    fromApi(dateTime: string) {
        return parseDateTimeFromApi(dateTime, this.locale)
    }

    fromApiOrUndefined(dateTime: string | null | undefined) {
        return dateTime ? parseDateTimeFromApi(dateTime, this.locale) : undefined
    }

    fromHourMinute(time: string, format: string = ISO_TIME_MINUTE_FORMAT) {
        return DateTime.fromFormat(time, format, { locale: this.locale })
    }

    toHourMinute(dateTime: DateTime) {
        return dateTime.toFormat(ISO_TIME_MINUTE_FORMAT)
    }

    toApi(dateTime: DateTime) {
        return dateTime.toFormat(API_DATETIME_FORMAT)
    }

    toDate(dateTime: DateTime) {
        return dateTime.toFormat(ISO_DATETIME_FORMAT)
    }

    toApiISOMinutesFormat(dateTime: DateTime) {
        return dateTime.toFormat(ISO_DATETIME_MINUTE_FORMAT)
    }

    private formatSafe(dateTime: DateTime, formatter: (dt: DateTime) => string): string {
        return dateTime.isValid ? formatter(dateTime) : this.invalidDateText
    }

    formatDate(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(this.dateFormat))
    }

    formatLongDate(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(this.longDateFormat))
    }

    /** Weekday plus the full date, for prose that names a specific day. Not capitalised: Danish
     *  keeps the weekday lowercase mid-sentence. */
    formatWeekdayLongDate(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(this.weekdayLongDateFormat))
    }

    formatDayAndDate(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => capitalize(dt.toFormat(this.dayAndDateFormat)))
    }

    formatTime(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(this.timeFormat))
    }

    formatTimeInterval(interval: Readonly<{ start: string; end: string }>) {
        const startDt = this.fromISO(interval.start)
        const endDt = this.fromISO(interval.end)
        if (!startDt.isValid || !endDt.isValid) return undefined
        return {
            start: this.formatTime(startDt),
            end: this.formatTime(endDt),
        }
    }

    formatTimeHms(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(this.timeFormatHms))
    }

    formatDateTime(dateTime: DateTime) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(this.dateTimeFormat))
    }

    formatDateTimeSafe(dateTime: DateTime | undefined) {
        const res = dateTime && dateTime.isValid ? dateTime.toFormat(this.dateTimeFormat) : undefined
        return res
    }

    formatCustom(dateTime: DateTime, format: string) {
        return this.formatSafe(dateTime, (dt) => dt.toFormat(format))
    }

    // handling special ISO duration format PT1H35M
    fromDurationString(time: string) {
        return Duration.fromISO(time)
    }

    formatDuration(duration: Duration) {
        if (duration.hours > 0) {
            return duration.toFormat(this.durationHourMinuteFormat)
        } else if (duration.minutes > 0) {
            return duration.toFormat(this.durationMinuteFormat)
        } else {
            return duration.toFormat(this.durationSecondsFormat)
        }
    }

    formatPeriod(start: DateTime, end: DateTime) {
        if (!start.isValid || !end.isValid) {
            return this.invalidDateText
        }
        if (start.hasSame(end, "day")) {
            return `${this.formatDate(start)} ${this.formatTime(start)} - ${this.formatTime(end)}`
        } else {
            return `${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
        }
    }

    getHourCycle() {
        return this.hourCycle
    }

    getAmPm = () => {
        const loc = new Intl.DateTimeFormat(this.locale, { hour: "numeric", hourCycle: this.hourCycle })
        return loc.resolvedOptions().hour12 ?? false
    }

    formatCompactPeriod(startDate: string, endDate: string) {
        const start = this.fromApi(startDate)
        const end = this.fromApi(endDate)
        if (!start.isValid || !end.isValid) return this.invalidDateText
        if (start.hasSame(end, "day")) {
            return `${start.toFormat(this.compactPeriod)} - ${this.formatTime(end)}`
        } else {
            return `${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
        }
    }
    formatShortRelative(dateTime: DateTime): string {
        const diff = dateTime.diffNow(["years", "months", "weeks", "days", "hours", "minutes", "seconds"]).negate()
        const u = this.shortRelativeUnits

        if (diff.years >= 1) return `${Math.round(diff.years)}${u.y}`
        if (diff.months >= 1) return `${Math.round(diff.months)}${u.mo}`
        if (diff.weeks >= 1) return `${Math.round(diff.weeks)}${u.w}`
        if (diff.days >= 1) return `${Math.round(diff.days)}${u.d}`
        if (diff.hours >= 1) return `${Math.round(diff.hours)}${u.h}`
        if (diff.minutes >= 1) return `${Math.round(diff.minutes)}${u.m}`

        return `${Math.max(1, Math.round(diff.seconds))}${u.s}`
    }

    formatCompactPeriodFromDateTime(start: DateTime, end: DateTime) {
        if (!start.isValid || !end.isValid) return this.invalidDateText
        if (start.hasSame(end, "day")) {
            const date = start.toFormat("dd/LL")
            const startTime = this.formatTime(start)
            const endTime = this.formatTime(end)

            return `${date} ${startTime} - ${endTime}`
        } else {
            return `${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
        }
    }
}

export function useDateTime() {
    const selectedLocale = useAppSelector(selectSelectedLocale)
    const { l10n } = useAppLocalization()
    const factory = useMemo(() => {
        // gets translations
        return new DateTimeFactory({
            locale: selectedLocale,
            dateFormat: l10n.getString("date-time-format-date"),
            longDateFormat: l10n.getString("date-time-format-long-date"),
            weekdayLongDateFormat: l10n.getString("date-time-format-weekday-long-date"),
            dayAndDateFormat: l10n.getString("date-time-format-day-and-date"),
            timeFormat: l10n.getString("date-time-format-time"),
            timeFormatHms: l10n.getString("date-time-format-time-hms"),
            dateTimeFormat: l10n.getString("date-time-format-date-and-time"),
            durationHourMinuteFormat: l10n.getString("date-time-format-duration-hours-minutes"),
            durationMinuteFormat: l10n.getString("date-time-format-duration-minutes"),
            durationSecondsFormat: l10n.getString("date-time-format-duration-seconds"),
            hourCycle: l10n.getString("date-time-hour-cycle"),
            compactPeriod: l10n.getString("date-time-compact-period-start"),
            shortRelativeUnits: l10n.getString("date-time-short-relative-units"),
            invalidDateText: l10n.getString("date-time-invalid-date"),
        })
    }, [selectedLocale, l10n])

    return factory
}
